import type { DeliveryPoint } from '@mealdesk/shared';

/**
 * Where a meal is left, chosen from a sheet.
 *
 * The same control the order screen uses, lifted out so the weekly menu can
 * ask the question too — once for a whole batch, and again for the one day
 * that is different. Somebody booking a week at a time usually collects in the
 * same place, but "usually" is not "always", and a batch that quietly sends
 * every day to one door is a batch that loses a meal on the day it moved.
 */
export default function PointSheet({
  title,
  note,
  points,
  value,
  onPick,
  onClose,
}: {
  title: string;
  note?: string;
  points: DeliveryPoint[];
  value: string;
  onPick: (pointId: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-panel-dark/40"
      />

      <div className="relative mx-auto w-full max-w-[430px] rounded-t-[34px] bg-white px-[22px] pt-6 pb-8 shadow-[0_-8px_28px_rgba(38,64,28,.18)]">
        <b className="block text-[19px] tracking-[-0.03em] md:text-[21px]">{title}</b>
        {note && <p className="mt-1 text-[13.5px] text-muted md:text-[15px]">{note}</p>}

        <fieldset className="mt-4 flex flex-col gap-2.5">
          <legend className="sr-only">{title}</legend>

          {points.length === 0 && (
            <p className="rounded-tile bg-panel px-4 py-4 text-[13.5px] text-muted md:text-[15px]">
              No pickup points are set up for your site yet. Ask your company admin.
            </p>
          )}

          {points.map((point) => {
            const selected = point.id === value;
            return (
              <label
                key={point.id}
                className={`flex cursor-pointer items-center gap-3.5 rounded-control px-4 py-4 ${
                  selected
                    ? 'border-[1.5px] border-brand-bright bg-panel'
                    : 'border-[1.5px] border-transparent bg-panel'
                }`}
              >
                <input
                  type="radio"
                  name="sheetPoint"
                  className="sr-only"
                  checked={selected}
                  onChange={() => {
                    onPick(point.id);
                    onClose();
                  }}
                />
                <span
                  aria-hidden
                  className={`flex size-[22px] shrink-0 items-center justify-center rounded-full border-2 ${
                    selected ? 'border-brand-bright' : 'border-line-dashed'
                  }`}
                >
                  <span
                    className={`size-[11px] rounded-full ${
                      selected ? 'bg-brand-bright' : 'bg-transparent'
                    }`}
                  />
                </span>
                <span className="min-w-0 leading-[1.3]">
                  <b className="block text-[15px] md:text-[16.5px]">{point.name}</b>
                  <span className="text-[13px] text-muted md:text-[14.5px]">{point.note}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        <button
          type="button"
          onClick={onClose}
          className="mt-4 flex h-[52px] w-full items-center justify-center rounded-control border border-line-strong text-[15px] font-extrabold md:text-[16.5px]"
        >
          Close
        </button>
      </div>
    </div>
  );
}
